<template>
  <div
    class="fixed top-header left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue"
  >
    <HeaderShadow></HeaderShadow>
    <div class="p-4">
      <form @submit.prevent="addSession">
        <div class="flex pb-4">
          <div class="mr-2 w-1/3">
            <router-link to="/local-admin/sessions">
              <ButtonSecondary
                type="button"
                :name="$t('buttons.cancel')"
                :disabled="loading"
              ></ButtonSecondary>
            </router-link>
          </div>
          <div class="ml-2 w-2/3">
            <Button
              type="submit"
              :name="$t('buttons.addSession')"
              :disabled="
                !required(session.name) || !required(session.roomId) || loading
              "
              :loading="loading"
            ></Button>
          </div>
        </div>

        <div
          class="bg-nucleus-white transition-all"
          :class="{ 'opacity-50': loading }"
        >
          <div class="flex items-center px-4 py-6 border-b">
            <div
              class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-session"
            ></div>
            <div class="text-2xl capitalize">
              {{ $t("global.defaults.newSession") }}
            </div>
          </div>
          <div class="px-4 py-6 overflow-y-auto scroll-box">
            <Input
              id="name"
              v-model:value="session.name"
              input-type="text"
              :label="$t('inputs.name.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.name.placeholder')"
              autocomplete="off"
              required
            />
            <Select
              id="ors"
              v-model:value="session.roomId"
              :label="$t('inputs.room.label')"
              :options="operatingRooms"
              required
            />
            <Input
              id="department"
              v-model:value="session.department"
              input-type="text"
              :label="$t('inputs.department.label')"
              icon=""
              :max-length="inputMaxLengths.department"
              :placeholder="$t('inputs.department.placeholder')"
              autocomplete="off"
            />
            <Textarea
              id="notes"
              v-model:value="session.notes"
              :label="$t('inputs.notes.label')"
              :max-length="inputMaxLengths.notes"
              :placeholder="$t('inputs.notes.placeholder')"
            />
          </div>
        </div>
      </form>

      <ErrorBox
        v-if="errorHeading || errorContent"
        :heading="errorHeading"
        :content="errorContent"
        :fixed="errorFixed"
        @closeError="
          errorHeading = '';
          errorContent = '';
        "
      ></ErrorBox>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src
import Textarea from "@/components/utils/Textarea.vue"; // @ is an alias to /src
import ListItemBasic from "@/components/utils/ListItemBasic.vue"; // @ is an alias to /src
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import ErrorBox from "@/components/utils/ErrorBox.vue";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    Button,
    ButtonSecondary,
    HeaderShadow,
    Input,
    Textarea,
    ListItemBasic,
    Select,
    ErrorBox,
  },
  data() {
    return {
      session: {
        name: "",
        roomId: undefined,
        roomName: "",
        department: "",
        notes: "",
      },
      invitees: [],
      loading: false,
      showParticipants: false,
      users: null,
      operatingRooms: null,
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  mounted() {
    document.title =
      this.$t("global.defaults.addSession") +
      " - " +
      this.$t("global.defaults.browserTitle");
    this.getSessionOptions();
  },
  methods: {
    addSession() {
      this.loading = true;

      this.session.roomId = Number(this.session.roomId);

      let roomName = "";

      if (this.operatingRooms) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        roomName = this.operatingRooms.filter((room: any) => {
          return room.id === this.session.roomId;
        })[0].name;
      }

      const data = {
        name: this.session.name,
        roomId: this.session.roomId,
        roomName: roomName,
        department: this.session.department,
        notes: this.session.notes,
      };

      this.$store
        .dispatch("addSession", data)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response) {
            this.loading = false;
            this.$store.dispatch("changeUpdate", "session");
            this.$router.push({
              name: "sessionsDetail",
              params: { sessionId: response.data.id },
            });
            console.log("add session", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    getSessionOptions() {
      this.$store
        .dispatch(
          "getSessionOptions",
          this.$store.getters.getUserObject.attributes["custom:tenancy"]
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response.data) {
            this.operatingRooms = response.data.operatingRooms;
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    required(input: string | number, inputName: string) {
      if (input === undefined) {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }

      if (typeof input === "string" && input.length === 0) {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }

      if (inputName) this.error[inputName] = "";
      return true;
    },
  },
})
export default class SessionsAdd extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 278px);
}
</style>
